import React, { useEffect, useRef } from 'react';

export type ConfirmModalVariant = 'default' | 'danger' | 'warning';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** @deprecated utiliser `variant="danger"` */
  isDangerous?: boolean;
  variant?: ConfirmModalVariant;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  onConfirm,
  onCancel,
  isDangerous = false,
  variant,
}) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const resolvedVariant: ConfirmModalVariant = variant || (isDangerous ? 'danger' : 'default');

  useEffect(() => {
    if (!isOpen) return;
    cancelButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const confirmButtonClass =
    resolvedVariant === 'danger' ? 'btn-danger' :
    resolvedVariant === 'warning' ? 'btn-warning' :
    'btn-primary';

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div
        className={`confirm-modal confirm-modal-${resolvedVariant}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-message"
      >
        <h2 id="confirm-modal-title" className="confirm-modal-title">{title}</h2>
        <div id="confirm-modal-message" className="confirm-modal-message">{message}</div>
        <div className="confirm-modal-actions">
          <button
            ref={cancelButtonRef}
            className="btn btn-secondary"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            className={`btn ${confirmButtonClass}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
